import { lazy } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';

import { ROLE_FEATURES, RoleProtected } from 'watchtower-ui/components/common/RoleProtected';
import { FeatureFlagContextManager, useFeatureFlag } from 'watchtower-ui/contexts/FeatureFlagContext';
import Unauthorized from 'watchtower-ui/pages/Unauthorized/Unauthorized';
import { basePath } from 'watchtower-ui/utils/Constants';

const DynamicDashboardPage = lazy(() => import('watchtower-ui/pages/HistoricalPages/DynamicDashboardPage'));
const HistoricalHome = lazy(() => import('watchtower-ui/pages/HistoricalPages/HistoricalHome'));
const HistoricalKpi = lazy(() => import('watchtower-ui/pages/HistoricalPages/kpi/HistoricalKpi'));
const MomentumAnalysis = lazy(() => import('watchtower-ui/pages/HistoricalPages/momentum/MomentumAnalysis'));
const HistoricalWaterfall = lazy(() => import('watchtower-ui/pages/HistoricalPages/waterfall/HistoricalWaterfall'));
const RegionalInsights = lazy(() => import('watchtower-ui/pages/HistoricalPages/regional/RegionalInsights'));
const LearnedCurvesWrapper = lazy(() => import('watchtower-ui/pages/HistoricalPages/curves/LearnedCurvesWrapper'));
const HistoricalDecomp = lazy(() => import('watchtower-ui/pages/HistoricalPages/decomp/HistoricalDecomp'));
const InputImpactInsights = lazy(() => import('watchtower-ui/pages/HistoricalPages/input_impact/InputImpactInsights'));
const DriverAnalysis = lazy(() => import('watchtower-ui/pages/HistoricalPages/driver_analysis/DriverAnalysis'));
const OptimizationPageContainer = lazy(() => import('watchtower-ui/pages/OptimizationPages/OptimizationPageContainer'));
const OptimizationHome = lazy(() => import('watchtower-ui/pages/OptimizationPages/OptimizationHome'));
const OptimizationPlanning = lazy(() => import('watchtower-ui/pages/OptimizationPages/planning/OptimizationPlanning'));
const PodOptimizer = lazy(() => import('watchtower-ui/pages/OptimizationPages/podOptimizer/PodOptimizer'));
const EvaluateExistingPlan = lazy(
  () => import('watchtower-ui/pages/OptimizationPages/planning/evaluateExistingPlan/EvaluateExistingPlan'),
);
const EvaluatePlanDeepDive = lazy(
  () => import('watchtower-ui/pages/OptimizationPages/planning/evaluateExistingPlan/EvaluatePlanDeepDive'),
);
const OptimizationPlanCurve = lazy(() => import('watchtower-ui/pages/OptimizationPages/view_plan_curve/ViewPlanCurve'));
const OptimizationDeepDiveCurve = lazy(
  () => import('watchtower-ui/pages/OptimizationPages/curve_deep_dive/CurveDeepDive'),
);
const Homepage = lazy(() => import('watchtower-ui/pages/Homepage/Homepage'));
const DocumentationPage = lazy(() => import('watchtower-ui/pages/Documentation/DocumentationPage'));

const redirectWithQuery = (to: string) => {
  const RedirectWithQuery = () => {
    const { search } = useLocation();

    return <Navigate to={`${to}${search}`} replace />;
  };

  return RedirectWithQuery;
};

export const PodOptimizerRoute = () => {
  const [showPodOptimizer] = useFeatureFlag('SHOW_POD_OPTIMIZER');
  const { search } = useLocation();

  const fallback = <Navigate to={{ pathname: '/optimization', search }} replace />;

  return showPodOptimizer ? (
    <RoleProtected {...ROLE_FEATURES.POD_OPTIMIZER} fallback={fallback}>
      <PodOptimizer />
    </RoleProtected>
  ) : (
    fallback
  );
};

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <FeatureFlagContextManager>
          <Outlet />
        </FeatureFlagContextManager>
      ),
      children: [
        {
          index: true,
          Component: Homepage,
        },
        {
          path: 'help/:path',
          Component: DocumentationPage,
          handle: {
            label: 'Documentation',
          },
        },
        {
          path: 'historical',
          Component: DynamicDashboardPage,
          handle: {
            label: 'Historical Home',
          },
          children: [
            {
              index: true,
              Component: HistoricalHome,
            },
            {
              path: 'waterfall',
              Component: HistoricalWaterfall,
              handle: {
                label: 'Waterfall',
              },
            },
            {
              path: 'momentum',
              Component: MomentumAnalysis,
              handle: {
                label: 'Momentum Analysis',
              },
            },
            {
              path: 'kpi',
              Component: HistoricalKpi,
              handle: {
                label: 'Combine KPI',
              },
            },
            {
              path: 'regional',
              Component: RegionalInsights,
              handle: {
                label: 'Regional Insights',
              },
            },
            {
              path: 'decomp',
              Component: HistoricalDecomp,
              handle: {
                label: 'Historical Decomposition',
              },
            },
            {
              path: 'roicurve',
              Component: redirectWithQuery('/optimization/plan'),
            },
            {
              path: 'input_impact',
              Component: redirectWithQuery('../input-impact'),
            },
            {
              path: 'input-impact',
              Component: InputImpactInsights,
              handle: {
                label: 'Input Impact Insights',
              },
            },
            {
              path: 'learned-curves',
              Component: LearnedCurvesWrapper,
              handle: {
                label: 'Learned Curves',
                featureFlag: 'SHOW_LEARNED_CURVES',
              },
            },
            {
              path: 'driver-analysis',
              Component: DriverAnalysis,
              handle: {
                label: 'Driver Analysis',
              },
            },
          ],
        },
        {
          path: 'optimization',
          Component: OptimizationPageContainer,
          handle: {
            label: 'Optimization Home',
          },
          children: [
            {
              index: true,
              Component: OptimizationHome,
            },
            {
              path: 'plan',
              Component: Outlet,
              handle: {
                label: 'Create New Plan',
              },
              children: [
                {
                  index: true,
                  Component: OptimizationPlanning,
                },
                {
                  path: 'view',
                  Component: Outlet,
                  handle: {
                    label: 'View Curve For Plan',
                  },
                  children: [
                    {
                      index: true,
                      Component: OptimizationPlanCurve,
                    },
                    {
                      path: 'deep-dive',
                      Component: OptimizationDeepDiveCurve,
                      handle: {
                        label: 'View Deep Dive For Plan',
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: 'evaluate',
              Component: Outlet,
              handle: {
                label: 'Evaluate Existing Plan',
              },
              children: [
                {
                  index: true,
                  Component: EvaluateExistingPlan,
                },
                {
                  path: 'deep-dive',
                  Component: EvaluatePlanDeepDive,
                  handle: {
                    label: 'Deep Dive Results',
                  },
                },
              ],
            },
            {
              path: 'pods',
              Component: PodOptimizerRoute,
              handle: {
                label: 'POD Optimizer',
                featureFlag: 'SHOW_POD_OPTIMIZER',
              },
            },
          ],
        },
        {
          path: 'unauthorized',
          Component: Unauthorized,
        },
      ],
    },
  ],
  { basename: basePath },
);

export default router;
